import clsx from "clsx";
import { useState } from "react";
import CopyIcon from "@mixitone/icons/clipboard.svg";
import TickIcon from "@mixitone/icons/tick.svg";
import { Button } from "./Button";

function htmlToPlainText(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent ?? div.innerText ?? "";
}

interface ClipboardButtonProps {
  /** Plain text to copy. Also used as fallback when `html` is provided but write() fails. */
  text: string;
  /** When set, copies as rich text: writes both text/html and text/plain so pasting into email preserves formatting. */
  html?: string;
  title?: string;
  className?: string;
  /** Optional visible label next to the icon (improves hit target and clarity). */
  label?: string;
}

export function ClipboardButton({ text, html, title = "Copy", className, label }: ClipboardButtonProps) {
  const [showTick, setShowTick] = useState(false);

  const handleCopy: React.MouseEventHandler<HTMLButtonElement> = async (event) => {
    event.preventDefault();

    try {
      if (html && navigator.clipboard.write) {
        const plain = htmlToPlainText(html);
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([plain], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(text);
      }
      setShowTick(true);
      setTimeout(() => setShowTick(false), 1500);
    } catch (error) {
      console.error("Failed to copy text:", error);
    }
  };

  return (
    <Button
      kind="link"
      onClick={handleCopy}
      title={title}
      aria-label={title}
      className={clsx(
        "min-h-9 min-w-9 shrink-0 items-center justify-center px-1.5",
        label && "text-xs",
        className,
      )}
    >
      <span className="relative inline-flex h-4 w-4 shrink-0">
        <TickIcon
          className={clsx("absolute h-4 w-4 stroke-green-600 transition-opacity", {
            "opacity-0": !showTick,
            "opacity-100": showTick,
          })}
        />
        <CopyIcon
          className={clsx("absolute h-4 w-4 transition-opacity", {
            "opacity-100": !showTick,
            "opacity-0": showTick,
          })}
        />
      </span>
      {label && <span>{showTick ? "Copied" : label}</span>}
    </Button>
  );
}
